import { useState, useCallback } from 'react';
import { SummaryGrid } from './SummaryGrid';
import { SnapCarousel } from './SnapCarousel';
import type { HoleInfo, HoleScore, Team } from '@/lib/golfEngine';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ModalPrototypeProps {
  holes: HoleInfo[];
  teams: Team[];
  scores: HoleScore[];
  onScoreChange: (playerId: string, holeNumber: number, score: number | null) => void;
}

export function ModalPrototype({ holes, teams, scores, onScoreChange }: ModalPrototypeProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleHoleTap = useCallback((holeIndex: number) => {
    setActiveIndex(holeIndex);
    setOpen(true);
  }, []);

  return (
    <div className="h-full overflow-y-auto" data-testid="prototype-modal">
      <SummaryGrid
        holes={holes}
        teams={teams}
        scores={scores}
        onHoleTap={handleHoleTap}
        activeHoleIndex={open ? activeIndex : null}
      />

      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]"
            onClick={() => setOpen(false)}
            data-testid="modal-backdrop"
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-testid="modal-container">
            <div
              className={cn(
                'pointer-events-auto w-[92vw] max-w-md max-h-[68vh] overflow-y-auto',
                'bg-background rounded-2xl shadow-2xl border',
                'animate-in fade-in-0 zoom-in-95 duration-200'
              )}
              data-testid="modal-card"
            >
              <div className="sticky top-0 z-10 flex justify-end p-2 bg-background/80 backdrop-blur-sm rounded-t-2xl">
                <button
                  onClick={() => setOpen(false)}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                  data-testid="modal-btn-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-2 pb-4">
                <SnapCarousel
                  holes={holes}
                  teams={teams}
                  scores={scores}
                  currentIndex={activeIndex}
                  onIndexChange={setActiveIndex}
                  onScoreChange={onScoreChange}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
